import type { SubmitEventHandler } from 'react';
import type { OnboardingModalOptions } from './options';

import { use, useCallback, useId, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import z from 'zod';

import { getFlotiqMainPageUrl, getTestProps } from '../../lib/helpers';

import Button from '../../components/Button/Button';
import Loader from '../../components/Loader/Loader';

import { ModalInstanceContext } from '../../contexts/ModalContext';

import { FlotiqBadgeLogo } from '../../images/shapes';

import { useAppForm } from '../../form-hooks/form';
import AdditionalDataModalInput from './AdditionalDataModaInput';
import { getOnboardingModalOptions } from './options';

const defaultValues = (isFormForExistingUsers: boolean = false) => ({
  workType: '',
  occupation: '',
  country: '',
  companyName: '',
  legalAgreements: isFormForExistingUsers,
  agreeNewsUpdates: false,
});

export type AdditionalDataFormType = ReturnType<typeof defaultValues> & {
  privacyPolicy: boolean;
  termsOfService: boolean;
};

interface AdditionalDataModalContentProps {
  handlePutAdditionalDataModalData: (
    payload: AdditionalDataFormType,
  ) => Promise<void>;
  handleDeclineAdditionalDataModal: () => Promise<void>;
  testId: string;
  existingUser?: boolean;
}

const legalAgreementsLinksClasses =
  'font-bold text-slate-900 border-b border-indigo-950 cursor-pointer' +
  ' hover:text-slate-900 hover:decoration-0 hover:no-underline dark:text-slate-200 dark:border-white' +
  ' focus:no-underline focus:text-slate-900';

const AdditionalDataModalContent = ({
  handlePutAdditionalDataModalData,
  handleDeclineAdditionalDataModal,
  testId,
  existingUser = true,
}: AdditionalDataModalContentProps) => {
  const { t, i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const modalInstance = use(ModalInstanceContext);
  const formId = useId();

  const handleAdditionalDataSubmit = useCallback(
    async (payload: AdditionalDataFormType) => {
      setIsLoading(true);
      await handlePutAdditionalDataModalData(payload);
      setIsLoading(false);
      modalInstance.resolve?.(true);
    },
    [handlePutAdditionalDataModalData, modalInstance, setIsLoading],
  );

  const handleAskMeLaterButton = useCallback(async () => {
    setIsLoading(true);
    await handleDeclineAdditionalDataModal();
    setIsLoading(false);
    modalInstance.resolve?.(true);
  }, [handleDeclineAdditionalDataModal, modalInstance]);

  const { countries, workType, occupation } = useMemo<OnboardingModalOptions>(
    () => getOnboardingModalOptions(t, i18n.language),
    [t, i18n],
  );

  const validationSchema = useMemo(
    () =>
      z.object({
        companyName: z.string(),
        workType: z
          .string()
          .min(1, t('AdditionalDataModal.Form.InvalidOption')),
        occupation: z
          .string()
          .min(1, t('AdditionalDataModal.Form.InvalidOption')),
        country: z.string().min(1, t('AdditionalDataModal.Form.InvalidOption')),
        legalAgreements: z.boolean().refine((val) => val === true, {
          message: t('AdditionalDataModal.Form.PrivacyPolicyError'),
        }),
        agreeNewsUpdates: z.boolean(),
      }),
    [t],
  );

  const form = useAppForm({
    defaultValues: defaultValues(existingUser),
    onSubmit: ({ value }) =>
      handleAdditionalDataSubmit({
        ...value,
        privacyPolicy: value.legalAgreements,
        termsOfService: value.legalAgreements,
      }),
    validators: {
      onSubmit: validationSchema,
    },
  });

  const handleSubmit = useCallback<SubmitEventHandler>(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      form.handleSubmit();
    },
    [form],
  );

  return (
    <div {...getTestProps(testId, 'additional-data-modal-content')}>
      <div className="relative w-full my-1 md:my-4">
        <FlotiqBadgeLogo className="w-10 h-10 md:w-11 md:h-11 absolute left-0 top-0" />
        <div
          className="flex flex-col items-center justify-center
        text-slate-900 align-middle text-center dark:text-slate-200"
        >
          <span className="text-center font-bold text-3xl/[32px] mb-5 w-3/4 md:mb-3 md:text-[28px]/[32px]">
            {existingUser
              ? t('AdditionalDataModal.HeadingForExistingUser')
              : t('AdditionalDataModal.Heading')}
          </span>
          <p className="text-sm md:text-base">
            {t('AdditionalDataModal.Paragraph')}
          </p>
        </div>
      </div>
      <form.AppForm>
        <form id={formId} onSubmit={handleSubmit} noValidate={true}>
          <div className="grid grid-cols-1 md:grid-cols-2 md:grid-rows-2 gap-y-2 gap-x-14 lg:gap-x-12">
            <form.AppField name="country">
              {(field) => (
                <AdditionalDataModalInput
                  as={field.Dropdown}
                  options={countries}
                  additionalContainerClasses="h-10"
                  header={t('AdditionalDataModal.Country')}
                  description={t('AdditionalDataModal.SelectOneMessage')}
                  required
                  isVirtualScrolling
                  {...getTestProps(testId, 'country', 'testId')}
                />
              )}
            </form.AppField>
            <form.AppField name="companyName">
              {(field) => (
                <AdditionalDataModalInput
                  as={field.Input}
                  additionalClasses="h-10"
                  additionalInputClasses="h-10"
                  description={t('AdditionalDataModal.YourIndustry')}
                  header={t('AdditionalDataModal.CompanyName')}
                  {...getTestProps(testId, 'company-name', 'testId')}
                />
              )}
            </form.AppField>
            <form.AppField name="workType">
              {(field) => (
                <AdditionalDataModalInput
                  options={workType}
                  as={field.Dropdown}
                  additionalContainerClasses="h-10"
                  header={t('AdditionalDataModal.RoleDescription')}
                  description={t('AdditionalDataModal.SelectOneMessage')}
                  required
                  {...getTestProps(testId, 'work-type', 'testId')}
                />
              )}
            </form.AppField>
            <form.AppField name="occupation">
              {(field) => (
                <AdditionalDataModalInput
                  as={field.Dropdown}
                  additionalContainerClasses="h-10"
                  options={occupation}
                  header={t('AdditionalDataModal.Industry')}
                  description={t('AdditionalDataModal.SelectOneMessage')}
                  required
                  {...getTestProps(testId, 'occupation', 'testId')}
                />
              )}
            </form.AppField>
          </div>
          <div className="space-y-4 mt-5">
            {!existingUser && (
              <form.AppField name="legalAgreements">
                {(field) => (
                  <field.Checkbox
                    additionalCheckboxClasses="w-5 h-5"
                    additionalLabelClasses={
                      "text-sm opacity-70 ml-4 capitalize text-slate-900 md:text-base after:content-['*']" +
                      ' after:text-red after:ml-1'
                    }
                    label={
                      <Trans
                        i18nKey="AdditionalDataModal.DataProcessingAgreement"
                        components={{
                          1: (
                            <a
                              href={getFlotiqMainPageUrl('terms-of-service')}
                              rel="noreferrer"
                              target="_blank"
                              aria-label="Terms of service"
                              className={legalAgreementsLinksClasses}
                            ></a>
                          ),
                          2: (
                            <a
                              href={getFlotiqMainPageUrl('privacy-policy')}
                              rel="noreferrer"
                              target="_blank"
                              aria-label="Privacy Policy"
                              className={legalAgreementsLinksClasses}
                            ></a>
                          ),
                        }}
                      />
                    }
                    {...getTestProps(testId, 'privacy-policy', 'testId')}
                  />
                )}
              </form.AppField>
            )}
            <form.AppField name="agreeNewsUpdates">
              {(field) => (
                <field.Checkbox
                  additionalCheckboxClasses="w-5 h-5"
                  additionalLabelClasses="text-sm opacity-70 ml-4 capitalize text-slate-900 md:text-base"
                  label={
                    <Trans
                      i18nKey="AdditionalDataModal.NewsLetterSubscription"
                      components={{
                        1: <strong />,
                      }}
                    />
                  }
                />
              )}
            </form.AppField>
          </div>
          <div
            className="w-full absolute flex justify-end p-4 left-0 bottom-0 border-t
           bg-white gap-5 dark:bg-slate-900 dark:border-slate-700 rounded-b-md"
          >
            {existingUser && (
              <Button
                type="button"
                buttonColor="borderlessBlue"
                additionalClasses="underline text-slate-900"
                disabled={isLoading}
                onClick={handleAskMeLaterButton}
              >
                {t('AdditionalDataModal.AskMeLater')}
              </Button>
            )}
            <Button
              type="submit"
              disabled={isLoading}
              iconPosition="end"
              iconImage={
                isLoading ? <Loader size="small" type="spinner-grid" /> : null
              }
            >
              {t('Global.Save')}
            </Button>
          </div>
        </form>
      </form.AppForm>
    </div>
  );
};

export default AdditionalDataModalContent;
